import { useQuery } from '@tanstack/react-query'
import {
  Users,
  CreditCard,
  FileText,
  TrendingUp,
  Loader2,
  AlertCircle
} from 'lucide-react'
import { adminApi, DashboardStats } from '@/api/admin'

interface StatCardProps {
  title: string
  value: number | string
  subtitle?: string
  icon: React.ElementType
  color: 'blue' | 'green' | 'amber' | 'purple'
}

function StatCard({ title, value, subtitle, icon: Icon, color }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
  }

  const iconClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
  }

  return (
    <div className={`rounded-xl border-2 p-6 ${colorClasses[color]}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-sm mt-1 opacity-70">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${iconClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  )
}

export function AdminDashboard() {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: adminApi.getDashboardStats,
    staleTime: 60 * 1000, // 1분
  })

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">데이터를 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
        <p className="text-slate-500 text-sm">관리자 권한이 필요합니다</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">관리자 대시보드</h1>
        <p className="text-slate-600 mt-1">시스템 전체 현황을 확인하세요</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="전체 사용자"
          value={stats?.totalUsers ?? 0}
          subtitle={`활성 ${stats?.activeUsers ?? 0}명`}
          icon={Users}
          color="blue"
        />
        <StatCard
          title="전체 구독"
          value={stats?.totalSubscriptions ?? 0}
          subtitle={`활성 ${stats?.activeSubscriptions ?? 0}건`}
          icon={CreditCard}
          color="green"
        />
        <StatCard
          title="전체 콘텐츠"
          value={stats?.totalContents ?? 0}
          subtitle={`발행됨 ${stats?.publishedContents ?? 0}개`}
          icon={FileText}
          color="amber"
        />
        <StatCard
          title="활성화율"
          value={stats?.totalUsers ? `${Math.round((stats.activeUsers / stats.totalUsers) * 100)}%` : '0%'}
          subtitle="사용자 기준"
          icon={TrendingUp}
          color="purple"
        />
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users by Type */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            사용자 유형별 현황
          </h2>
          <div className="space-y-3">
            {stats?.usersByType && Object.entries(stats.usersByType).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-slate-600 capitalize">{type}</span>
                <span className="font-semibold text-slate-900">{count}명</span>
              </div>
            ))}
            {(!stats?.usersByType || Object.keys(stats.usersByType).length === 0) && (
              <p className="text-slate-500 text-center py-4">데이터가 없습니다</p>
            )}
          </div>
        </div>

        {/* Subscriptions by Status */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-green-600" />
            구독 상태별 현황
          </h2>
          <div className="space-y-3">
            {stats?.subscriptionsByStatus && Object.entries(stats.subscriptionsByStatus).map(([status, count]) => {
              const statusColors: Record<string, string> = {
                active: 'text-green-600',
                paused: 'text-yellow-600',
                expired: 'text-slate-500',
                cancelled: 'text-red-600',
                suspended: 'text-orange-600',
              }
              return (
                <div key={status} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <span className={`capitalize ${statusColors[status] || 'text-slate-600'}`}>{status}</span>
                  <span className="font-semibold text-slate-900">{count}건</span>
                </div>
              )
            })}
            {(!stats?.subscriptionsByStatus || Object.keys(stats.subscriptionsByStatus).length === 0) && (
              <p className="text-slate-500 text-center py-4">데이터가 없습니다</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">빠른 작업</h2>
        <div className="flex flex-wrap gap-3">
          <a href="/users" className="btn-primary btn-md">
            사용자 관리
          </a>
          <a href="/subscriptions" className="btn-secondary btn-md">
            구독 관리
          </a>
          <a href="/contents" className="btn-secondary btn-md">
            콘텐츠 관리
          </a>
        </div>
      </div>
    </div>
  )
}





















